
import jsPDF from 'jspdf';
import { Contract } from '@/lib/types';
import { ContractService } from '@/lib/types/services';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';

export class ContractPdfGenerator {
  private doc: jsPDF;

  constructor() {
    this.doc = new jsPDF();
  }

  private addHeader(text: string) {
    this.doc.setFontSize(18);
    this.doc.text(text, 20, 20);
    this.doc.setFontSize(12);
    this.doc.line(20, 25, 190, 25);
  }

  private addSection(title: string, content: string, yPosition: number): number {
    this.doc.setFontSize(14);
    this.doc.text(title, 20, yPosition);
    this.doc.setFontSize(12);
    
    const lines = this.doc.splitTextToSize(content, 170);
    this.doc.text(lines, 20, yPosition + 7);
    
    return yPosition + (lines.length * 7) + 10;
  }

  private addServicesSection(services: ContractService[], yPosition: number): number {
    if (!services || services.length === 0) {
      return yPosition;
    }

    this.doc.setFontSize(14);
    this.doc.text('Contract Services', 20, yPosition);
    this.doc.setFontSize(12);
    
    yPosition += 10;

    // Table headers
    const headers = ['Service', 'Type', 'Quantity', 'Unit Cost', 'Total Cost'];
    const columnWidth = 35;
    let xPosition = 20;

    headers.forEach((header) => {
      this.doc.text(header, xPosition, yPosition);
      xPosition += columnWidth;
    });

    yPosition += 5;
    this.doc.line(20, yPosition, 190, yPosition);
    yPosition += 5;

    // Table rows
    services.forEach((service) => {
      if (!service.service) return;

      xPosition = 20;
      const unitCost = service.service.cost.toLocaleString();
      const totalCost = (service.service.cost * service.quantity).toLocaleString();
      const currency = service.service.currency?.symbol || '';
      
      const rowData = [
        service.service.name,
        service.service.service_type,
        service.quantity.toString(),
        `${unitCost} ${currency}`,
        `${totalCost} ${currency}`
      ];

      rowData.forEach((text) => {
        this.doc.text(text, xPosition, yPosition);
        xPosition += columnWidth;
      });

      yPosition += 7;
    });

    return yPosition + 10;
  }

  generateContractPdf(contract: Contract): Blob {
    this.doc = new jsPDF();
    
    this.addHeader('Contract Details');
    
    let yPosition = 40;
    
    yPosition = this.addSection('Supplier Information', 
      `${contract.supplierName}\n${contract.organisationnumber || ''}`, 
      yPosition
    );

    yPosition = this.addSection('Contract Type', 
      `${contract.type.name}\n${contract.type.description || ''}`,
      yPosition
    );

    yPosition = this.addSection('Financial Details',
      `Expected Cost: ${contract.expectedCost} ${contract.currency?.code || ''}\n` +
      `Payment Frequency: ${contract.paymentFrequency.interval} ${contract.paymentFrequency.unit}`,
      yPosition
    );

    yPosition = this.addSection('Dates',
      `Start Date: ${contract.signedDate.toLocaleDateString()}\n` +
      `Expiry Date: ${contract.expiryDate.toLocaleDateString()}`,
      yPosition
    );

    if (contract.contactPersons.length > 0) {
      const contactsText = contract.contactPersons
        .map(contact => `${contact.name} (${contact.role})\n${contact.email}`)
        .join('\n\n');
      
      yPosition = this.addSection('Contact Persons', contactsText, yPosition);
    }

    // Add services section if they exist and the feature is enabled
    if (contract.contractServices && contract.contractServices.length > 0) {
      // Note: We'll check the feature flag in the component that uses this generator
      yPosition = this.addServicesSection(contract.contractServices, yPosition);
    }

    // Add a signature section at the bottom
    const signatureY = 240;
    this.doc.line(20, signatureY, 90, signatureY);
    this.doc.text("Signature", 20, signatureY + 5);
    
    // Add footer with date and page number
    const today = new Date();
    this.doc.setFontSize(8);
    this.doc.text(`Generated on: ${today.toLocaleDateString()}`, 20, 280);
    this.doc.text("Page 1 of 1", 180, 280);

    return this.doc.output('blob');
  }
}

export const contractPdfGenerator = new ContractPdfGenerator();
